import { Fragment, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { ThemeProvider, useTheme } from '@emotion/react'
import { Container, Section, Button } from 'chaoskit/src/components'
import { Helmet } from 'react-helmet'
import { theme as ckTheme } from 'chaoskit/src/assets/styles/theme'
import { extendTheme } from 'chaoskit/src/helpers/Wrapper'
import { rgba, shade, tint } from 'polished'
import { text, link } from 'chaoskit/src/assets/styles/utility'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import {
  motion,
  useAnimation,
  useViewportScroll,
  useTransform,
} from 'framer-motion'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import Image from '~components/Image'
import Hero, { HeroContent } from '~partials/Hero'
import { ReactComponent as LogoSVG } from '~media/chaos-conf/2020/logo.svg'
import heroBackground from '~media/chaos-conf/2020/hero-background.svg'
import dots from '~media/chaos-conf/2020/dots.svg'
import cubes1 from '~media/chaos-conf/2020/cubes-1.svg'
import cubes2 from '~media/chaos-conf/2020/cubes-2.svg'
import cubes3 from '~media/chaos-conf/2020/cubes-3.svg'
import cubes4 from '~media/chaos-conf/2020/cubes-4.svg'
import { VideoModal } from '~components/Video'
import Data from '~components/Data'
import { stickyIt } from '~styles/utility'
import track from '~helpers/track'
import { getRandomInteger } from '~helpers/utility'

const chaosConf2020Theme = extendTheme(ckTheme, {
  color: {
    primary: {
      base: '#0D9A5C',
      get light() {
        return tint(0.9, this.base)
      },
      get dark() {
        return shade(0.1, this.base)
      },
      filter:
        'brightness(0) saturate(100%) invert(41%) sepia(84%) saturate(677%) hue-rotate(110deg) brightness(91%) contrast(90%)',
    },
  },
  settings: {
    button: {
      gradient: {
        enable: false,
      },
    },
  },
})

const patternStyles = (theme) => ({
  content: "''",
  position: 'absolute',
  zIndex: 0,
  background: `url(${dots})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '699px 166px',
  backgroundPosition: 'center center',
  opacity: theme.opacity.base,
})

const ChaosConf2020Hero = () => {
  const theme = useTheme()

  const { scrollY } = useViewportScroll()

  const y = useTransform(scrollY, [0, 600], ['0%', '25%'])

  return (
    <Hero
      compact
      css={{
        fontFamily: 'Fira Mono',
        backgroundColor: theme.color.dark.base,
        paddingBottom: 0,
        position: 'relative',
        overflow: 'hidden',

        '&::before': {
          ...patternStyles(theme),
          bottom: 0,
          left: 0,
          width: '80%',
          height: 200,
          filter: theme.color.light.filter,

          [theme.mq.medium]: {
            width: 400,
            height: 200,
          },
        },

        'h1, h2, h3, h4, h5, h6': {
          fontFamily: 'Space Mono',
          fontWeight: theme.fontWeight.base,
          textTransform: 'uppercase',
        },
      }}
    >
      <Image
        src={heroBackground}
        css={{
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
        initial={{ y: 0 }}
        style={{ y }}
      />
      <HeroContent
        css={{
          marginTop: theme.space.xlarge,
          textAlign: 'center',
        }}
      >
        <Container>
          <LogoSVG
            css={{
              width: 500,
            }}
          />
        </Container>
        <aside
          className={theme.settings.classes.trim}
          css={{
            background: `linear-gradient(to bottom, ${rgba(
              theme.color.dark.base,
              0
            )}, ${theme.color.dark.overlay})`,
            paddingTop: theme.space.base,
            paddingBottom: theme.space.base,
            marginTop: theme.space.large,

            [theme.mq.medium]: {
              paddingTop: theme.space.large,
              paddingBottom: theme.space.large,
            },
          }}
        >
          <Container>
            <h3>
              Chaos Conf is the largest Chaos Engineering event in history
            </h3>
            <p css={{ fontWeight: theme.fontWeight.bold }}>
              Watch all 18 talks from Chaos Conf 2020, and join us for the next
              Chaos Conf!
            </p>
          </Container>
        </aside>
      </HeroContent>
    </Hero>
  )
}

const VideoButton = ({ title, youtubeId, ...rest }) => {
  const [videoModalOpen, setVideoModalOpen] = useState(false)

  return (
    <Fragment>
      <Button
        type="primary"
        css={{
          fontFamily: 'Fira Mono',
        }}
        onClick={() => {
          track('Video recap watched', {
            label: `Chaos Conf 2020: ${title}`,
          })

          setVideoModalOpen(true)
        }}
        {...rest}
      >
        View talk
      </Button>
      <VideoModal
        open={videoModalOpen}
        setIsOpen={setVideoModalOpen}
        title="Customer video"
        src={`https://www.youtube.com/embed/${youtubeId}?modestbranding=1&rel=0&showinfo=0&autohide=1&autoplay=1&list=PLLIx5ktghjqJz97f6bpvV0qvkuOTsLGtU`}
      />
    </Fragment>
  )
}

VideoButton.propTypes = {
  title: PropTypes.string.isRequired,
  youtubeId: PropTypes.string.isRequired,
}

const Talk = ({ talk, flip }) => {
  const theme = useTheme()

  const [expanded, setExpanded] = useState(false)

  const controls = useAnimation()

  const cubeGraphic = useMemo(() => getRandomInteger(1, 4), [])

  const variants = {
    hidden: {
      height: 0,
      opacity: 0,
    },
    visible: {
      height: 'auto',
      opacity: 1,
    },
  }

  useUpdateEffect(() => {
    if (expanded) {
      controls.start('visible')
    } else {
      controls.start('hidden')
    }
  }, [expanded])

  return (
    <div
      css={[
        {
          display: 'grid',
          gap: theme.space.large,

          [theme.mq.medium]: {
            gap: theme.space.xlarge,
            gridTemplateColumns: '400px 1fr',
          },
        },

        flip && {
          [theme.mq.medium]: {
            gridTemplateColumns: '1fr 400px',
          },
        },
      ]}
    >
      <div
        css={[
          flip && {
            [theme.mq.medium]: {
              gridColumn: 2,
            },
          },
        ]}
      >
        <div
          css={{
            position: 'relative',
            margin: '0 auto',

            [theme.mq.small__max]: {
              width: 250,
            },

            [theme.mq.medium]: [stickyIt(theme)],

            '&::before': [
              {
                content: "''",
                width: '80%',
                height: '80%',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top',
                position: 'absolute',
                zIndex: -1,
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
              },

              cubeGraphic === 1 && {
                backgroundImage: `url(${cubes1})`,
              },

              cubeGraphic === 2 && {
                backgroundImage: `url(${cubes2})`,
              },

              cubeGraphic === 3 && {
                backgroundImage: `url(${cubes3})`,
              },

              cubeGraphic === 4 && {
                backgroundImage: `url(${cubes4})`,
              },
            ],
          }}
        >
          <Image src={talk.image} alt="" />
        </div>
      </div>

      <div
        css={[
          flip && {
            [theme.mq.medium]: {
              gridColumn: 1,
              gridRow: 1,
            },
          },
        ]}
      >
        <h3 css={{ ...theme.headingPreset.h2 }}>{talk.title}</h3>
        <div
          css={{
            color: theme.color.primary.base,
            fontWeight: theme.fontWeight.bold,

            a: [text.underline, link.base(theme)],
          }}
        >
          <Data>{talk.byline}</Data>
        </div>
        <div css={{ marginTop: theme.space.medium }}>
          {talk.teaserContent ? (
            <Fragment>
              <p>
                <Data>{talk.teaserContent}</Data>
              </p>
              <motion.div
                css={{ overflow: 'hidden' }}
                initial="hidden"
                animate={controls}
                variants={variants}
                transition={{
                  duration: theme.motion.timing.base,
                }}
              >
                <MDXRenderer>{talk.content}</MDXRenderer>
              </motion.div>
            </Fragment>
          ) : (
            <MDXRenderer>{talk.content}</MDXRenderer>
          )}
          {talk.teaserContent && (
            <Button
              css={{
                background: theme.color.primary.base,
                fontWeight: theme.fontWeight.bold,
                paddingLeft: theme.space.xsmall,
                paddingRight: theme.space.xsmall,
                color: theme.contrast.base,
              }}
              type="reset"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'less' : 'more'}
            </Button>
          )}
        </div>
        <div css={{ marginTop: theme.space.large }}>
          <VideoButton title={talk.title} youtubeId={talk.youtubeId} />
        </div>
      </div>
    </div>
  )
}

Talk.propTypes = {
  talk: PropTypes.object.isRequired,
  flip: PropTypes.bool,
}

const ChaosConf2020Talks = ({ talks }) => {
  const theme = useTheme()

  if (talks.length === 0) return null

  return (
    <main
      css={{
        fontFamily: 'Fira Mono',
        position: 'relative',

        '&::before, &::after': patternStyles(theme),

        [theme.mq.small__max]: {
          overflow: 'hidden',
        },

        '&::before': {
          top: '25%',
          right: 0,
          transform: 'translateX(125px) rotate(90deg)',
          filter: theme.color.dark.filter,
          height: 200,
          width: 500,
        },

        '&::after': {
          bottom: 0,
          left: 0,
          width: '80%',
          height: 200,

          [theme.mq.medium]: {
            width: 400,
          },
        },

        'h1, h2, h3, h4, h5, h6': {
          fontFamily: 'Space Mono',
          fontWeight: theme.fontWeight.base,
          textTransform: 'uppercase',
        },
      }}
    >
      <Section css={{ position: 'relative', zIndex: 1 }}>
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,

              [theme.mq.medium]: {
                gap: theme.space.xlarge,
              },
            }}
          >
            {talks.map((talk, index) => (
              <Talk key={talk.title} talk={talk} flip={index % 2 !== 0} />
            ))}
          </div>
        </Container>
      </Section>
    </main>
  )
}

ChaosConf2020Talks.propTypes = {
  talks: PropTypes.array.isRequired,
}

const ChaosConf2020 = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => (
  <BaseLayout headerProps={{ hasDefaultOverlay: true }}>
    <HelmetSEO
      title={pageData.title}
      description={pageData.description}
      image={pageData.socialMediaImage}
    />
    <Helmet>
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=Fira+Mono:wght@400;700&family=Space+Mono&display=swap"
        rel="stylesheet"
      />
    </Helmet>

    <ThemeProvider theme={chaosConf2020Theme}>
      <ChaosConf2020Hero />
      <ChaosConf2020Talks talks={pageData.talks} />
    </ThemeProvider>
  </BaseLayout>
)

ChaosConf2020.propTypes = {
  data: PropTypes.object.isRequired,
}

export default ChaosConf2020

export const pageQuery = graphql`
  query ChaosConf2020PageData {
    file(
      name: { eq: "chaos-conf__2020" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          talks {
            title
            byline
            teaserContent
            content
            youtubeId
            image
          }
        }
      }
    }
  }
`
